import APIConfig from "service/network/APIConfig";
import APIService from "service/network/APIService";
import Helper from "util/Helper";

/**
 * Drug schedule — client for pharmacy-dose-schedule-api. Zero new
 * backend types: everything the mockup needs is expressible against the
 * existing DosePlan / Dosedata shape (verified against a prod entry
 * 2026-08-25). See project_drug_schedule.md memory for the field
 * mapping + gotchas (status casing, string nextdosefrequency.value,
 * dosename per-dose for drug name, rescheduledate ignored).
 */
class DrugScheduleService {
  constructor() {
    this.apiService = new APIService();
    // Side effect: installs String.prototype.format for the URL templates.
    this.helper = new Helper();
  }

  /**
   * Fetch the full drug catalog. Server returns the whole list; the
   * picker does client-side fuzzy filtering.
   */
  listDrugs(onResponse, onError) {
    return this.apiService.get(
      APIConfig.drugSchedule.drugCatalog,
      (res) => onResponse(Array.isArray(res) ? res : []),
      onError,
    );
  }

  /** DosePlans for one patient. Empty list is a legitimate state. */
  list(patientId, onResponse, onError) {
    const url = APIConfig.drugSchedule.listByPatient.format(patientId);
    return this.apiService.get(
      url,
      (res) => onResponse(Array.isArray(res) ? res : []),
      onError,
    );
  }

  /**
   * Server-derived "next dose date" + "dose N of M" per active plan.
   * We use the list endpoint to render the table when we already have
   * the plans, so this is only a fallback / verification helper.
   */
  upcoming(patientId, onResponse, onError) {
    const url = APIConfig.drugSchedule.upcomingByPatient.format(patientId);
    return this.apiService.get(
      url,
      (res) => onResponse(Array.isArray(res) ? res : []),
      onError,
    );
  }

  /**
   * Create a new plan. Body is the full DosePlan the mockup produces:
   * pid, status:"Active", doses[], createdby (plain string).
   */
  create(body, onResponse, onError) {
    return this.apiService.post(
      APIConfig.drugSchedule.create,
      body, onResponse, onError,
    );
  }

  /**
   * Update actual dates + status. Backend map-updates only the five
   * fields it recognises; send them all every time so nothing gets
   * dropped on the wire (see controllers/drug.go:76).
   */
  update(body, onResponse, onError) {
    return this.apiService.put(
      APIConfig.drugSchedule.update,
      body, onResponse, onError,
    );
  }
}

export default DrugScheduleService;
